import { useState } from "react";

import { TinsmithLockup } from "@tinsmith/brand";

import styles from "./AuthScreen.module.css";
import { useSession } from "./session";
import { useDocumentTitle } from "../chrome/useDocumentTitle";
import { Button } from "../components/Button";
import { ApiError } from "../data/api/client";
import { isPasskeyCancellation, passkeysSupported } from "../data/api/webauthn";
import { Icon } from "../icons";

// Sign-in is passkey-only. There is no password / GitHub sign-in. New users
// don't sign in here — they follow an enrollment link (/join) to mint a passkey,
// which creates their account.

const messageFor = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.code === "network") {
      return "Can't reach the server. Is it running?";
    }
    return err.message;
  }
  return "Something went wrong. Please try again.";
};

export const AuthScreen = () => {
  const { signInWithPasskey } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const supported = passkeysSupported();
  useDocumentTitle("Sign in");

  const signIn = async (): Promise<void> => {
    if (busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await signInWithPasskey();
      // On success the session flips to authenticated and this screen unmounts.
    } catch (err) {
      if (!isPasskeyCancellation(err)) {
        setError(messageFor(err));
      }
      setBusy(false);
    }
  };

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <TinsmithLockup size={24} />
        </div>

        {!supported ? (
          <div className={styles.error}>
            This browser doesn't support passkeys. Open Tinsmith in a browser
            with Touch ID, Windows Hello, or a security key.
          </div>
        ) : null}
        {error ? <div className={styles.error}>{error}</div> : null}

        <Button
          className={styles.submit}
          disabled={busy || !supported}
          onClick={() => void signIn()}
          size="lg"
          variant="accent"
        >
          <Icon.Shield size={15} />{" "}
          {busy ? "Waiting for passkey…" : "Sign in with a passkey"}
        </Button>
      </div>
    </div>
  );
};
